import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import UserImage from '../../UserImage';
import { getUser } from '../../../utils/func';
import { subscribeSessionChanges } from '../../../hooks/useSessionIdentity';

const AvatarContext = createContext(null);

// Each page owns its footer layout, but the account photo must survive route
// changes. Keep one React-owned image mounted in a movable portal host. This
// retains the decoded image even when its HTTP response forbids caching.
export function FooterAvatarProvider({ children }) {
    const [host] = useState(() => {
        const element = document.createElement('span');
        element.style.display = 'contents';
        // The surrounding Link owns navigation. Portal events otherwise bubble
        // through the provider's React tree instead of that page's Link.
        element.style.pointerEvents = 'none';
        return element;
    });
    const [avatar, setAvatar] = useState(null);
    const context = useMemo(() => ({ host, setAvatar }), [host]);

    useEffect(() => {
        const syncSession = () => {
            const user = getUser();
            setAvatar(previous => {
                if (!user) return null;
                return previous?.owner === user.id && previous.picture === user.picture
                    ? previous : { owner: user.id, picture: user.picture };
            });
        };
        return subscribeSessionChanges(syncSession);
    }, []);

    return <AvatarContext.Provider value={context}>
        {children}
        {avatar && createPortal(<UserImage key={avatar.owner} picture={avatar.picture} loading="eager" />, host)}
    </AvatarContext.Provider>;
}

export default function FooterAvatar({ user }) {
    const context = useContext(AvatarContext);
    const slot = useRef(null);
    const { id: owner, picture } = user;

    useLayoutEffect(() => {
        if (!context) return;
        const target = slot.current;
        context.setAvatar(previous => previous?.owner === owner && previous.picture === picture
            ? previous : { owner, picture });
        target.appendChild(context.host);
        return () => {
            if (context.host.parentNode === target) target.removeChild(context.host);
        };
    }, [context, owner, picture]);

    return context ? <span ref={slot} style={{ display: 'contents' }} /> : <UserImage picture={picture} loading="eager" />;
}
